import { Link } from '@inertiajs/react'
import { createContext, useContext, type MouseEvent, type ReactNode } from 'react'
import { grapePath, placePath, wineryPath } from '../../lib/map_config'

/**
 * Something a panel can be opened for, and as much of it as the caller already
 * knows.
 *
 * `name` and `kind` are what make the panel's frame appear with the click
 * rather than with the response: a country's name is in the world layer, an
 * appellation's in the region features the shell holds, a producer's in the
 * winery layer, and a search result or a breadcrumb step carries its own. A
 * caller that knows neither says so by leaving them out, and the shell looks
 * them up.
 */
export interface PanelTarget {
  type: 'place' | 'winery' | 'grape'
  slug: string
  name?: string | null
  kind?: string | null
  /**
   * Whether the map has a mark of its own for it — a shape for a place, a dot
   * for a producer — where the shell can tell. A panel's kind line opens with
   * that mark, so the stand-in for a page still on its way has to know whether
   * to draw one, or the word beside it jumps when the page lands. Left out is
   * "not known", and the stand-in then draws one: nearly everything a reader
   * can choose has one, and everything they can click on the map does.
   */
  onMap?: boolean
  /**
   * A producer's dot as the winery layer is drawing it: its size class, and
   * whether that class is a stated figure. Only the layer knows, so only a
   * producer chosen from the map carries it.
   */
  dot?: { sizeClass: string | null; stated: boolean }
}

/**
 * The identity of a target, and of an open page. Typed, because a producer and
 * an appellation can share a slug (KTD15): Hanzell the winery opened from
 * Hanzell the hypothetical appellation is a different panel.
 */
export function panelKey(target: Pick<PanelTarget, 'type' | 'slug'>) {
  return `${target.type}:${target.slug}`
}

export function panelPath(target: Pick<PanelTarget, 'type' | 'slug'>) {
  if (target.type === 'place') return placePath(target.slug)
  if (target.type === 'winery') return wineryPath(target.slug)

  return grapePath(target.slug)
}

export interface PanelNavigation {
  /** Open the panel for this, showing its frame at once. */
  open: (target: PanelTarget) => void
  /**
   * The pointer is resting on this (or, with null, has left it), from a device
   * that can hover. The shell may fetch its page so the click is answered from
   * memory.
   */
  intend: (target: PanelTarget | null) => void
}

/**
 * How a link inside a panel reaches the shell.
 *
 * The page child is whatever Inertia mounted, so a panel cannot be handed the
 * shell's handlers as props. Null when there is no shell around it — a
 * component test, or a panel rendered on its own — and then a link is just a
 * link.
 */
export const PanelNavigationContext = createContext<PanelNavigation | null>(null)

// A click the browser should keep for itself: a new tab, a new window, a
// download, or anything but the primary button.
const modified = (event: MouseEvent) =>
  event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey

interface PanelLinkProps {
  target: PanelTarget
  className?: string
  children: ReactNode
}

/**
 * A link from one panel to another, whatever it opens. `PlaceLink` and
 * `WineryLink` are this with the target spelled out, so the two cannot come to
 * behave differently — a breadcrumb that showed its panel at once and a winery
 * row that waited for the server would be two products in one card.
 *
 * It is a real link first — an `href`, so it opens in a new tab, reads to a
 * screen reader as where it goes, and works with no shell at all — and inside
 * the shell a plain click takes the same path a click on the map does. That is
 * the difference a reader feels: the title changes to the thing they chose the
 * moment they choose it, instead of the old panel sitting there for a round
 * trip with nothing to say the click was heard.
 *
 * `replace`, always. A panel is already open here by definition, so every link
 * out of it replaces: on a map, Back means "close this". With no shell that is
 * the link's own `replace`; inside one it is the shell's to decide, and it
 * decides the same (`openPanel`).
 *
 * INSIDE THE SHELL IT IS A PLAIN ANCHOR, and that is a speed decision. There
 * Inertia's `Link` has nothing left to do — the shell issues the visit, and a
 * click the handler below leaves alone is one `Link` leaves alone too — but
 * it still costs its keep on every row: a dozen memos, two effects and a URL
 * parse apiece. A place's panel holds these by the hundred, most of them
 * inside a closed disclosure: 123 places under the United States, 75 wineries
 * under a valley. Measured in Chrome against a production build, medians of
 * twelve: 63 rows of a winery list mount in 1.0 ms as anchors and 1.6 ms as
 * `Link`s, and with the CPU slowed four times in 5.1 ms against 7.0. (A run
 * on a busier machine put both higher and further apart; these are the quiet
 * ones.)
 */
function PanelLink({ target, className, children }: PanelLinkProps) {
  const navigation = useContext(PanelNavigationContext)
  const href = panelPath(target)

  // No shell: Inertia's own link, replacing.
  if (!navigation) {
    return (
      <Link href={href} replace className={className}>
        {children}
      </Link>
    )
  }

  return (
    <a
      href={href}
      className={className}
      onClick={(event: MouseEvent) => {
        if (modified(event)) return

        // Prevented, so the browser does not follow the link and there is
        // one visit for the click, issued by the shell.
        event.preventDefault()
        navigation.open(target)
      }}
      onMouseEnter={() => navigation.intend(target)}
      onMouseLeave={() => navigation.intend(null)}
    >
      {children}
    </a>
  )
}

interface PlaceLinkProps {
  place: { slug: string; name: string; kind?: string | null }
  className?: string
  children: ReactNode
}

/**
 * A link from one panel to a place: a breadcrumb step, a child, the appellation
 * a producer names. See `PanelLink` for what a click on it does.
 */
export function PlaceLink({ place, className, children }: PlaceLinkProps) {
  return (
    <PanelLink
      target={{ type: 'place', slug: place.slug, name: place.name, kind: place.kind ?? null }}
      className={className}
    >
      {children}
    </PanelLink>
  )
}

interface WineryLinkProps {
  winery: { slug: string; name: string }
  /**
   * The dot the caller is drawing beside the name, when it draws one. A caller
   * that can draw it knows two things the stand-in panel needs and the shell
   * may not: that the map has a mark for this producer at all, and which —
   * the winery layer holds only the frame it last fetched, and holds cells
   * rather than producers when the camera is far out, which is where a place's
   * panel most often opens.
   */
  dot?: PanelTarget['dot']
  className?: string
  children: ReactNode
}

/**
 * A link from a panel to a producer: a row of the wineries a place holds.
 * Choosing it is choosing its dot on the map — the stand-in panel at once, the
 * history entry replaced, the page fetched when the pointer rests on the row
 * (`PanelLink`) — and, when its page lands, the camera a dot gets: left where
 * it is if the dot is already in plain view, taken to it if not.
 */
export function WineryLink({ winery, dot, className, children }: WineryLinkProps) {
  return (
    <PanelLink
      target={{ type: 'winery', slug: winery.slug, name: winery.name, ...(dot ? { onMap: true, dot } : {}) }}
      className={className}
    >
      {children}
    </PanelLink>
  )
}

interface GrapeLinkProps {
  grape: { slug: string; name: string }
  className?: string
  children: ReactNode
}

/**
 * A link from a panel to a grape: a variety a winery says it grows. A grape
 * has no mark on the map, so the stand-in panel is told so (`onMap: false`)
 * and draws none, and the camera stays where it is, as it does for a grape
 * chosen from the search box.
 */
export function GrapeLink({ grape, className, children }: GrapeLinkProps) {
  return (
    <PanelLink target={{ type: 'grape', slug: grape.slug, name: grape.name, onMap: false }} className={className}>
      {children}
    </PanelLink>
  )
}
